import { maxLoanFromMonthly, pmt } from '@/pages/blueprint-lite/utils/finance';

import type { CalculationSettings } from '../config/calculationSettings';
import type {
  Citizenship,
  CurrencyCode,
  LenderAssessment,
  LenderPolicy,
  PropertyUse,
} from '../types';

export interface LenderPipelineInput {
  citizenship: Citizenship;
  currency: CurrencyCode;
  baseAud: number;
  bonusAud: number;
  grossAud: number;
  rentalMonthly: number;
  livingMonthly: number;
  liabilitiesMonthly: number;
  loanAmount: number;
  lvrPct: number;
  propertyUse: PropertyUse;
  settings: CalculationSettings;
}

const VERDICT_WEIGHT: Record<LenderAssessment['verdict'], number> = {
  pass: 3,
  amber: 2,
  fail: 1,
};

function maxLvrFor(policy: LenderPolicy, citizenship: Citizenship): number {
  if (citizenship === 'australian_citizen')
    return policy.maxLvrAuCitizen ?? 0;
  if (citizenship === 'australian_pr_visa') return policy.maxLvrAuPr ?? 0;
  return policy.maxLvrForeignNational ?? 0;
}

function shadeFor(table: Record<string, number>, currency: string): number {
  return table[currency] ?? table._ ?? 0.5;
}

function assessLender(
  policy: LenderPolicy,
  input: LenderPipelineInput,
): LenderAssessment {
  const maxLvrPct = maxLvrFor(policy, input.citizenship) * 100;

  if (maxLvrPct === 0) {
    return {
      name: policy.name,
      displayName: policy.displayName,
      tier: policy.tier,
      logoUrl: policy.logoUrl,
      verdict: 'fail',
      reason: 'Lender does not service this citizenship type.',
      maxLvr: maxLvrPct,
      lvrOk: false,
      shade: 0,
      baseIncome: 0,
      bonusIncome: 0,
      totalIncome: 0,
      haircut: 1,
      rentalAccepted: 0,
      monthlyIncome: 0,
      surplus: 0,
      assessmentRate: 0,
      maxLoan: 0,
      dtiOk: false,
      dtiDisplay: 'n/a',
      assessmentRateBuffer: policy.assessmentRateBuffer,
      floorRate: policy.floorRate,
      auRentalShade: policy.auRentalShade,
    };
  }

  const lvrOk = input.lvrPct <= maxLvrPct;
  const baseShade = shadeFor(policy.baseSalaryShade, input.currency);
  const bonusShade = shadeFor(policy.bonusSalaryShade, input.currency);
  const baseIncome = input.baseAud * baseShade;
  const bonusIncome = input.bonusAud * bonusShade;
  const totalIncome = baseIncome + bonusIncome;
  const haircut =
    input.grossAud > 0
      ? (input.grossAud - totalIncome) / input.grossAud
      : 0;

  const rentalAccepted =
    input.propertyUse === 'investment'
      ? input.rentalMonthly * policy.auRentalShade
      : 0;
  const monthlyIncome = totalIncome / 12 + rentalAccepted;

  const lenderRepay =
    input.loanAmount > 0
      ? pmt(input.loanAmount, policy.productRate, input.settings.loanTermMonths)
      : 0;
  const surplus =
    monthlyIncome - input.livingMonthly - input.liabilitiesMonthly - lenderRepay;

  const assessmentRate = Math.max(
    policy.productRate + policy.assessmentRateBuffer,
    policy.floorRate ?? 0,
  );
  const available = Math.max(
    0,
    monthlyIncome - input.livingMonthly - input.liabilitiesMonthly,
  );
  const maxLoan = maxLoanFromMonthly(available, assessmentRate, input.settings.loanTermMonths);

  let dtiOk = true;
  let dtiDisplay = '-';
  if (typeof policy.dti === 'number') {
    const dv = totalIncome > 0 ? input.loanAmount / totalIncome : Infinity;
    dtiOk = dv <= policy.dti;
    dtiDisplay = `${dv.toFixed(2)}x`;
  } else if (policy.dscr) {
    const d = input.livingMonthly + input.liabilitiesMonthly + lenderRepay;
    const dscr = d > 0 ? monthlyIncome / d : 0;
    dtiOk = dscr >= policy.dscr.minimum;
    dtiDisplay = `DSCR ${dscr.toFixed(2)}x`;
  }

  const verdict: LenderAssessment['verdict'] =
    lvrOk && surplus >= 0 && dtiOk
      ? 'pass'
      : lvrOk && dtiOk
        ? 'amber'
        : 'fail';

  return {
    name: policy.name,
    displayName: policy.displayName,
    tier: policy.tier,
    logoUrl: policy.logoUrl,
    verdict,
    reason:
      verdict === 'pass'
        ? 'Meets LVR, serviceability and DTI/DSCR.'
        : verdict === 'amber'
          ? 'Borderline serviceability — review with broker.'
          : 'Does not currently meet policy.',
    maxLvr: maxLvrPct,
    lvrOk,
    shade: baseShade,
    baseIncome,
    bonusIncome,
    totalIncome,
    haircut,
    rentalAccepted,
    monthlyIncome,
    surplus,
    assessmentRate,
    maxLoan,
    dtiOk,
    dtiDisplay,
    assessmentRateBuffer: policy.assessmentRateBuffer,
    floorRate: policy.floorRate,
    auRentalShade: policy.auRentalShade,
  };
}

export interface LendersOutput {
  lenders: LenderAssessment[];
  passCount: number;
  reviewCount: number;
  failCount: number;
  borrowingLow: number;
  borrowingHigh: number;
}

export function assessLenders(
  input: LenderPipelineInput,
  panel: LenderPolicy[],
): LendersOutput {
  const assessed = panel.map((p) => assessLender(p, input));
  const ranked = [...assessed].sort((a, b) => {
    const w = VERDICT_WEIGHT[b.verdict] - VERDICT_WEIGHT[a.verdict];
    if (w !== 0) return w;
    return b.maxLoan - a.maxLoan;
  });

  const passing = ranked.filter((l) => l.verdict === 'pass');
  const viable = passing.length > 0
    ? passing
    : ranked.filter((l) => l.verdict === 'amber');

  const borrowingLow =
    viable.length > 0 ? Math.min(...viable.map((l) => l.maxLoan)) : 0;
  const borrowingHigh =
    viable.length > 0 ? Math.max(...viable.map((l) => l.maxLoan)) : 0;

  return {
    lenders: ranked,
    passCount: ranked.filter((l) => l.verdict === 'pass').length,
    reviewCount: ranked.filter((l) => l.verdict === 'amber').length,
    failCount: ranked.filter((l) => l.verdict === 'fail').length,
    borrowingLow,
    borrowingHigh,
  };
}
